import AwesomeDebouncePromise from "awesome-debounce-promise";

import useFormOperations from "@Totum/QuestionnaireBuilder/Application/hooks/useFormOperations";
import { IQuestion } from "@Totum/QuestionnaireBuilder/Domain/types";
import { showQuestionUpdatedNotification } from "@Totum/QuestionnaireBuilder/Presentation//helpers";
import { useCallback } from "react";

/**
 * Presents the `DesignerRow` component with the UI model and handlers.
 */
const useDesignerRowPresenter = (questionId: IQuestion["questionId"]) => {
    // ** Hooks ** //
    const {
        isDeleting,
        questionnaire,

        deleteQuestion,
        updateChoices,
        updateCorrectChoice,
        updateQuestionIsScored,
        updateQuestionTitle,
        updateQuestionType,
    } = useFormOperations();

    const question = questionnaire.questions.find((q) => q.questionId === questionId);

    // ** Handlers ** //

    // Create debounced versions of the update functions
    const debouncedUpdateChoices = AwesomeDebouncePromise(updateChoices, 500);
    const debouncedUpdateCorrectChoice = AwesomeDebouncePromise(updateCorrectChoice, 500);
    const debouncedUpdateQuestionTitle = AwesomeDebouncePromise(updateQuestionTitle, 1500);
    const debouncedUpdateQuestionType = AwesomeDebouncePromise(updateQuestionType, 500);

    /**
     * Handles the change of choices for a question
     * @param questionId The ID of the question
     * @param values The new values for the choices
     */
    const handleChoicesChange = useCallback(
        async (values: string[]): Promise<void> => {
            const operationResult = await debouncedUpdateChoices(questionId, values);
            if (operationResult.hasAction) {
                showQuestionUpdatedNotification(operationResult.isSuccess, question?.title);
            }
        },
        [debouncedUpdateChoices, question?.title, questionId]
    );

    /**
     * Handles the change of the 'isCorrect' property for a choice
     * @param questionId The ID of the question
     * @param choiceText The text of the choice to update
     */
    const handleChoiceIsCorrectChange = useCallback(
        async (choiceText: string): Promise<void> => {
            const operationResult = await debouncedUpdateCorrectChoice(questionId, choiceText);
            if (operationResult.hasAction) {
                showQuestionUpdatedNotification(operationResult.isSuccess, question?.title);
            }
        },
        [debouncedUpdateCorrectChoice, question?.title, questionId]
    );

    /**
     * Handles the deletion of a question
     * @param questionId The ID of the question
     */
    const handleDeleteRowClick = useCallback(async (): Promise<void> => {
        const operationResult = await deleteQuestion(questionId);

        if (operationResult.hasAction) {
            showQuestionUpdatedNotification(operationResult.isSuccess, question?.title, "delete");
        }
    }, [deleteQuestion, question?.title, questionId]);

    /**
     * Handles the change of the 'isScored' property for a question
     * @param questionId The ID of the question
     * @param isChecked Boolean indicating if the question is scored
     */
    const handleQuestionIsScoredChange = useCallback(
        async (isChecked: boolean): Promise<void> => {
            const operationResult = await updateQuestionIsScored(questionId, isChecked);
            if (operationResult.hasAction) {
                showQuestionUpdatedNotification(operationResult.isSuccess, question?.title);
            }
        },
        [question?.title, questionId, updateQuestionIsScored]
    );

    /**
     * Handles the change of the title of a question
     * @param questionId The ID of the question
     * @param title The new title for the question
     */
    const handleQuestionTitleChange = useCallback(
        async (title: string) => {
            const isNew = !question?.questionId;
            const operationResult = await debouncedUpdateQuestionTitle(questionId, title);

            const isValidQuestion = !!title && !!question?.questionType;

            if (isValidQuestion && operationResult.hasAction) {
                showQuestionUpdatedNotification(operationResult.isSuccess, title, isNew ? "create" : "update");
            }
        },
        [debouncedUpdateQuestionTitle, question?.questionId, question?.questionType, questionId]
    );

    /**
     * Handles the change of the type of a question
     * @param questionId The ID of the question
     * @param questionType The new type for the question
     */
    const handleQuestionTypeChange = useCallback(
        async (questionType: string) => {
            const isNew = !question?.questionId;

            //     getPresetText(questionType)?.map((text, index) => ({
            //         choiceId: index.toString(),
            //         choiceText: text,
            //         orderIndex: index,
            //     })) ?? [];
            const operationResult = await debouncedUpdateQuestionType(questionId, questionType);
            if (operationResult.hasAction) {
                showQuestionUpdatedNotification(
                    operationResult.isSuccess,
                    question?.title,
                    isNew ? "create" : "update"
                );
            }
        },
        [debouncedUpdateQuestionType, question?.questionId, question?.title, questionId]
    );

    // ** Exports ** //

    return {
        handlers: {
            handleChoiceIsCorrectChange,
            handleChoicesChange,
            handleDeleteRowClick,
            handleQuestionIsScoredChange,
            handleQuestionTitleChange,
            handleQuestionTypeChange,
        },
        uiModel: {
            question,

            highlightedChoice: question?.choices.find((choice) => choice.isCorrect)?.choiceId ?? null,
            previewChoices:
                question?.questionType === "boolean" ? question.choices.slice(0, 2) : question?.choices ?? [],
            tooltipTitle: !question?.isScored ? "You can only select the correct answer for scored questions" : "",

            // flags
            isDeleting,
        },
    };
};

export default useDesignerRowPresenter;
